/**
 * Buy AI writing credits: a free quantity at a flat, linear price — see
 * `shared/src/aiCredits.ts` for why this has no packs, unlike `MediaCreditsCard`'s.
 *
 * The balance is credited by Stripe's webhook, not by this dialog's redirect: a tab closed after
 * paying must not lose what was paid for, so this only starts the checkout and sends the browser
 * there. `AiCreditsNavRow` refetches the balance when the app regains focus.
 */

import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import type { AiCreditBalance } from '@ftk-connector/shared'
import { priceForAiCredits } from '@ftk-connector/shared'

import { errorMessage } from '../../lib/api'
import { useFormat, useT } from '../../lib/i18n'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { Input } from '../../components/ui/Input'
import { InlineError } from '../../components/ui/ErrorState'
import { Modal } from '../../components/ui/Modal'
import { useBuyAiCredits } from './queries'

export interface AiCreditsBuyDialogProps {
  open: boolean
  onClose: () => void
  brandId: string
  balance: AiCreditBalance
}

/** Round numbers worth a one-tap shortcut — the field underneath still takes any quantity. */
const QUICK_PICKS = [10, 50, 100, 500] as const

export const AiCreditsBuyDialog = ({
  open,
  onClose,
  brandId,
  balance,
}: AiCreditsBuyDialogProps): ReactElement => {
  const t = useT()
  const f = useFormat()
  const buy = useBuyAiCredits()
  const [quantity, setQuantity] = useState(balance.minPurchase)

  const invalid =
    !Number.isInteger(quantity) || quantity < balance.minPurchase || quantity > balance.maxPurchase
  const price = f.currencyCents(priceForAiCredits(quantity), balance.currency)

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (invalid) return
    buy.mutate(
      { brandId, credits: quantity },
      {
        onSuccess: (result) => {
          window.location.assign(result.url)
        },
      },
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('shell.aiCredits.buyTitle')}
      description={t('shell.aiCredits.buyDescription', {
        price: f.currencyCents(balance.pricePerCreditMinorUnits, balance.currency),
      })}
      size="sm"
      footer={
        <Button type="submit" form="ai-credits-buy" variant="primary" loading={buy.isPending} disabled={invalid}>
          {t('shell.aiCredits.buyConfirm', { price })}
        </Button>
      }
    >
      <form id="ai-credits-buy" noValidate onSubmit={submit} className="flex flex-col gap-4">
        {buy.isError ? <InlineError>{errorMessage(buy.error)}</InlineError> : null}

        <div className="flex flex-wrap gap-2">
          {QUICK_PICKS.filter(
            (pick) => pick >= balance.minPurchase && pick <= balance.maxPurchase,
          ).map((pick) => (
            <Button
              key={pick}
              type="button"
              size="sm"
              variant={quantity === pick ? 'primary' : 'secondary'}
              onClick={() => {
                setQuantity(pick)
              }}
            >
              {t('shell.aiCredits.quickPick', {
                credits: f.integer(pick),
                price: f.currencyCents(priceForAiCredits(pick), balance.currency),
              })}
            </Button>
          ))}
        </div>

        <Field
          label={t('shell.aiCredits.quantityLabel')}
          hint={t('shell.aiCredits.quantityHint', {
            min: f.integer(balance.minPurchase),
            max: f.integer(balance.maxPurchase),
          })}
          error={
            invalid
              ? t('shell.aiCredits.quantityError', {
                  min: f.integer(balance.minPurchase),
                  max: f.integer(balance.maxPurchase),
                })
              : undefined
          }
        >
          <Input
            type="number"
            inputMode="numeric"
            min={balance.minPurchase}
            max={balance.maxPurchase}
            value={quantity}
            onChange={(event) => {
              setQuantity(Number(event.target.value))
            }}
          />
        </Field>

        <p className="t-small text-ink-mute">{t('shell.aiCredits.noExpiry')}</p>
      </form>
    </Modal>
  )
}
